import { useEffect, useRef, useState } from 'react';
import useModalContext from 'components/context/modal';
import uniqBy from 'utils/uniqBy';
import syncApi from '../api/sync';

const useMyCreations = (accessToken, skus = [], transform = item => item) => {
  const modalContext = useModalContext();
  const api = useRef(syncApi(accessToken));

  const [data, setData] = useState();
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [sinceIdToNextPage, setSinceIdToNextPage] = useState();

  const fetchData = async ({ sinceId = undefined, reload = false } = {}) => {
    const {
      data: creationsData,
      error: creationsError,
      nextPageSinceId,
    } = await api.current.getProductDrafts(skus, sinceId);

    if (creationsData) {
      setData(val =>
        uniqBy(
          [
            ...(reload ? [] : val ?? []),
            ...creationsData.reduce((acc, item) => {
              const transformedItem = transform(item);
              if (transformedItem) {
                acc.push(transformedItem);
              }

              return acc;
            }, []),
          ],
          'id',
        ),
      );
      setSinceIdToNextPage(nextPageSinceId);
    } else {
      setError(creationsError);
      setSinceIdToNextPage(null);
    }

    setIsLoading(false);
  };

  const deleteCreation = async id => {
    const { error: deleteError } = await syncApi(accessToken).removeProductDraft(id);

    if (deleteError) {
      modalContext.toast(error, { type: 'warning' });
      return;
    }

    setData(value => value.filter(item => item.id !== id));
    modalContext.toast(`Creation #${id} has been deleted.`, { type: 'success' });
  };

  const loadMore = async () => {
    await fetchData({ sinceId: sinceIdToNextPage });
  };

  const reload = async () => {
    await fetchData({ reload: true });
  };

  const clear = async () => {
    await setData([]);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    error,
    isLoading,
    isEmpty: !isLoading && data.length === 0,
    hasNextPage: !!sinceIdToNextPage,
    deleteCreation,
    loadMore,
    reload,
    clear,
  };
};

export default useMyCreations;
